// @flow
import formatters from 'lib/formatters'
import { httpDelete, httpPost } from 'lib/http'
import { setBanner, setLoad, setUnload } from 'lib/notification/actions'
import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import Header from './Header.js'
import CheckForm from '../Forms/CheckForm.js'
import DirectDepositForm from '../Forms/DirectDepositForm.js'
import ReviewForm from '../../Distributions/components/Main/forms/ReviewForm.js'
import MainCtaFooter from '../../Distributions/components/Footer/MainCtaFooter.js'
import styles from '../../Distributions/components/Main/styles.css'

function DepositReceivedForm() {
  const [form, setForm] = useState({
    account: '',
    deposit_type: '',
    amount: '',
    is_refund: undefined,
    is_transfer: undefined,
    take_enrollment_fee: undefined,
    deposit_to_cash: false,
    note: '',
    bank_date: '',
    fee_exceptions: '',
    schedule: '',
    admin_fee: '',
    fee_rate: '',
    enroll_fee: '',
    events: [],
    files: [],
    notes: [],
    trust_account: '',
    deposited_bank_account: '',
    cash_account: '',
  })

  const [temporaryId] = useState(uuidv4())
  const [files, setFiles] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const [formDisabled] = useState(false)
  const [approveButtonText] = useState('Save and Create Another')
  const [disapproveButtonText] = useState('Ready for Quick Deposit')
  const [showUnableToProcess, setShowUnableToProcess] = useState(false)
  const [checkingBalance, setCheckingBalance] = useState('')
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const updateForm = useCallback((e) => {
    const { name, value, type, checked } = e.target
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }, [])

  const buildUploadParams = async (fileName, file) => {
    const toBase64 = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = (error) => reject(error)
      })

    const data = await toBase64(file)
    const params = {
      content_type: file.type,
      name: fileName,
      data: data.split(',')[1],
    }

    return params
  }

  const onUploadClick = (fileName, file) => {
    buildUploadParams(fileName, file).then((params) => {
      const url = `/admin/deposits/${temporaryId}/files`

      dispatch(setLoad())
      httpPost(url, params)
        .then((data) => {
          dispatch(setBanner('File uploaded'))
          setFiles(data.files)
          dispatch(setUnload())
        })
        .catch((err) => {
          dispatch(setBanner(`Error saving - ${err}`, 'danger'))
          dispatch(setUnload())
        })
    })
  }

  const onDeleteClick = (fileId) => {
    const url = `/admin/deposits/files/${fileId}`

    httpDelete(url)
      .then((res) => {
        setFiles(files.filter((file) => file.id !== fileId))
        dispatch(setBanner('File deleted'))
      })
      .catch((err) => {
        dispatch(setBanner(`Error deleting - ${err}`, 'danger'))
      })
  }

  const hasFormData = () => {
    return form.account || form.amount || files.length > 0 || form.note || form.trust_account || form.cash_account || form.deposit_to_cash
  }

  const canSave = () => {
    if (submitting) return false
    if (!form.deposit_type) return false

    if (form.deposit_type == 'check' && files.length > 0) {
      return true
    } else if (
      form.bank_date &&
      form.amount &&
      form.deposited_bank_account &&
      (form.trust_account || form.deposit_to_cash === true) &&
      form.is_refund !== undefined &&
      form.is_transfer !== undefined
    ) {
      return true
    }

    return false
  }

  const cleanParams = useCallback((obj) => {
    const cleaned = {}
    Object.keys(obj).forEach((key) => {
      const value = obj[key]

      if (typeof value === 'boolean') {
        cleaned[key] = value
      } else if (value !== '' && value !== undefined && value !== null) {
        cleaned[key] = value
      }
    })
    return cleaned
  }, [])

  const buildSubmissionParams = useCallback(() => {
    const baseParams = {
      temporary_id: temporaryId,
      deposit_type: form.deposit_type,
      amount: form.amount,
      is_refund: form.is_refund,
      is_transfer: form.is_transfer,
      take_enrollment_fee: form.take_enrollment_fee,
      deposit_to_cash: form.deposit_to_cash,
      note: form.note,
      bank_date: form.bank_date,
    }

    if (form.cash_account) {
      baseParams.cash_account = form.cash_account
    }

    if (form.trust_account?.number) {
      baseParams.account_number = form.trust_account.number
    }
    if (form.trust_account) {
      baseParams.trust_account = form.trust_account
    }

    if (form.deposited_bank_account) {
      baseParams.deposited_bank_account = form.deposited_bank_account
    }

    return baseParams
  }, [form, temporaryId])

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault()
      if (!canSave()) return

      setSubmitting(true)

      const params = cleanParams(buildSubmissionParams())

      dispatch(setLoad())

      httpPost('/admin/deposits', params)
        .then(({ deposit }) => {
          const friendlyStatus = formatters.legibleDepositStatus(deposit.status, deposit.next_status).toUpperCase().replace('_', ' ')
          dispatch(setBanner(`Deposit ID ${deposit.id} saved in ${friendlyStatus}`))
          setSubmitting(false)
          dispatch(setUnload())

          window.location.reload()
        })
        .catch((err) => {
          dispatch(setBanner(`Error saving - ${err}`, 'danger'))
          setSubmitting(false)
          dispatch(setUnload())
        })
    },
    [buildSubmissionParams, canSave, dispatch, cleanParams],
  )

  const handleConfirmationConfirm = useCallback(() => {
    handleSubmit({ preventDefault: () => {} })
  }, [handleSubmit])

  const isSaveRequired = useCallback(() => {
    return hasFormData()
  }, [hasFormData])

  const renderDepositTypeForm = () => {
    const formProps = {
      form,
      updateForm,
      formDisabled: false,
      onUploadClick,
      files,
      onDeleteClick,
      shouldShowNote: true,
    }

    switch (form.deposit_type) {
      case 'wire':
        return <DirectDepositForm {...formProps} />
      case 'check':
        return <CheckForm {...formProps} />
      case 'direct_deposit':
        return <DirectDepositForm {...formProps} />
      default:
        return <CheckForm {...formProps} />
    }
  }

  const ctaFormProps = {
    form,
    formDisabled,
    disapproveButtonText,
    approveButtonText,
    disapproveButtonVariant: 'deposit',
    files,
    nextStatus: 'quick_deposit',
    handleConfirmationConfirm,
    isSaveRequired,
    lockedForEdit: null,
    handleSubmit,
    showUnableToProcess,
    setShowUnableToProcess,
    canSave,
    submitting,
    showOnlyApproveButton: true,
  }

  return (
    <>
      <div className={styles.contentContainer}>
        <Header title="DEPOSIT" status="Receive" statusLabel="Current Status:" />
      </div>

      <form className={styles.form}>
        {renderDepositTypeForm()}
        <ReviewForm
          form={{
            ...form,
            notes: form.notes || [],
            events: form.events || [],
            files: files || [],
          }}
          showNotes={true}
          checkingBalance={checkingBalance}
          setCheckingBalance={setCheckingBalance}
          formDisabled={formDisabled}
        />
      </form>

      <div className={styles.footerContainer}>
        <MainCtaFooter {...ctaFormProps} />
      </div>
    </>
  )
}

export default DepositReceivedForm
